// Pure helpers for the school plan enquiry form: options, prefill, validation and the request body.
// Kept free of React so the rules can be tested on their own.

export const ROLE_OPTIONS = [
  'Teacher',
  'Head of Department / Faculty',
  'Teaching & Learning / Curriculum Leader',
  'School Leader',
  'IT / Digital Learning',
  'Other',
];

export const ALL_SUBJECTS = 'All subjects';
export const OTHER_SUBJECT = 'Other';
export const SUBJECT_OPTIONS = [
  ALL_SUBJECTS,
  'English',
  'Humanities',
  'Science',
  OTHER_SUBJECT,
];

export const STUDENT_COUNT_OPTIONS = [
  'Under 100',
  '100–499',
  '500–999',
  '1,000+',
];

export const DEMO_URL =
  'https://calendly.com/d/zhj-c6q-pkq/jeddle-demonstration';
export const SALES_PHONE_DISPLAY = '02 7908 3835';
export const SALES_PHONE_HREF = 'tel:+61279083835';

export const LIMITS = {
  name: 100,
  email: 254,
  schoolName: 200,
  phone: 30,
  otherSubjects: 200,
  note: 2000,
};

export const EMPTY_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  schoolName: '',
  role: '',
  phone: '',
  subjects: [],
  otherSubjects: '',
  studentCount: '',
  note: '',
};

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Profile subject names (e.g. "English Advanced", "Modern History") mapped onto the form's chips.
const SUBJECT_FAMILIES = [
  ['English', /english|literature|literacy|eal|esl/i],
  ['Science', /science|biology|chemistry|physics|earth|psychology/i],
  [
    'Humanities',
    /history|geography|economics|business|legal|civics|society|religion|studies of religion|commerce|politics|philosophy|humanities|hsie|social/i,
  ],
];

const subjectFamily = (subjectName) =>
  SUBJECT_FAMILIES.find(([, pattern]) => pattern.test(subjectName))?.[0];

/**
 * Chips for the subjects on the teacher's profile. Subjects that fit no chip select "Other"
 * and are written into the Other text field, so nothing the profile knows is dropped.
 */
export const subjectsFromProfile = (profileSubjects = []) => {
  const names = profileSubjects
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  const families = names.map(subjectFamily);
  const unmatched = names.filter((_, index) => !families[index]);
  const chips = SUBJECT_OPTIONS.filter(
    (option) =>
      families.includes(option) ||
      (option === OTHER_SUBJECT && unmatched.length > 0)
  );
  return { subjects: chips, otherSubjects: unmatched.join(', ') };
};

/**
 * What the server already knows about the teacher, used only to fill fields the teacher has not
 * typed into yet: a slow prefill must never overwrite what they have started entering.
 */
export const applyPrefill = (values, prefill) => {
  if (!prefill) return values;
  const fromProfile = subjectsFromProfile(prefill.subjects || []);
  const fillText = (key, value) =>
    values[key].trim()
      ? values[key]
      : String(value || '').slice(0, LIMITS[key] ?? 200);
  return {
    ...values,
    firstName: fillText('firstName', prefill.firstName),
    lastName: fillText('lastName', prefill.lastName),
    email: fillText('email', prefill.email),
    schoolName: fillText('schoolName', prefill.schoolName),
    phone: fillText('phone', prefill.phone),
    subjects: values.subjects.length ? values.subjects : fromProfile.subjects,
    otherSubjects: values.otherSubjects.trim()
      ? values.otherSubjects
      : fromProfile.otherSubjects,
  };
};

/** Picking "All subjects" clears the rest; picking any subject clears "All subjects". */
export const toggleSubject = (subjects, subject) => {
  const selected = subjects.includes(subject);
  if (subject === ALL_SUBJECTS) return selected ? [] : [ALL_SUBJECTS];
  const rest = subjects.filter((item) => item !== ALL_SUBJECTS);
  return selected
    ? rest.filter((item) => item !== subject)
    : [...rest, subject];
};

const phoneDigits = (phone) => phone.replace(/\D/g, '');

const phoneError = (phone) => {
  if (!phone.trim()) return 'Add a number so we can reach you.';
  const digits = phoneDigits(phone).length;
  return digits < 8 || digits > 15
    ? 'Check the number. We need a full phone number.'
    : null;
};

const emailError = (email) => {
  if (!email.trim()) return 'Add an email we can send your quote to.';
  return EMAIL_SHAPE.test(email.trim()) ? null : 'Check the email address.';
};

/** Field → message for every rule the values break; empty when the form can be sent. */
export const validate = (values) => {
  const errors = {
    firstName: values.firstName.trim() ? null : 'Add your first name.',
    email: emailError(values.email),
    schoolName: values.schoolName.trim()
      ? null
      : 'Add your school so we can price your plan.',
    role: values.role ? null : 'Pick the closest match.',
    phone: phoneError(values.phone),
    subjects: values.subjects.length
      ? null
      : 'Pick at least one, or All subjects.',
    otherSubjects:
      values.subjects.includes(OTHER_SUBJECT) && !values.otherSubjects.trim()
        ? 'Tell us which subjects.'
        : null,
    studentCount: values.studentCount ? null : 'A rough number is fine.',
  };
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message)
  );
};

/** Contact fields live behind "Edit" only while they are filled in and valid. */
export const contactNeedsEditing = (values) => {
  const errors = validate(values);
  return Boolean(errors.firstName || errors.email);
};

/** The body for POST /users/school-plan-request/details. */
export const enquiryPayload = (values, context = {}) => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  email: values.email.trim(),
  schoolName: values.schoolName.trim(),
  role: values.role,
  phone: values.phone.trim(),
  subjects: values.subjects,
  otherSubjects: values.subjects.includes(OTHER_SUBJECT)
    ? values.otherSubjects.trim()
    : '',
  studentCount: values.studentCount,
  note: values.note.trim(),
  source: context.source || '',
  trigger: context.trigger || '',
});

export const initials = (firstName, lastName) =>
  `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();
